/**
 * Sticky left sidebar — section navigation using react-router NavLink.
 *
 * Menu items are filtered by user role. "superadmin" sees everything;
 * "sales" sees Dashboard/Ventas/Devoluciones/Clientes; "user" only sees
 * Ventas and Devoluciones; "restricted" sees just the dashboard.
 */
import {
  BarChart3,
  Building2,
  Calendar,
  DollarSign,
  Leaf,
  Package,
  RotateCcw,
  ShoppingCart,
  Terminal,
  Users,
} from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'

const MENU = [
  { to: '/', label: 'Dashboard', icon: Calendar, roles: ['superadmin', 'sales', 'restricted'], end: true },
  { to: '/sales', label: 'Ventas', icon: DollarSign, roles: ['superadmin', 'sales', 'user'] },
  { to: '/returns', label: 'Devoluciones', icon: RotateCcw, roles: ['superadmin', 'sales', 'user'] },
  { to: '/purchases', label: 'Compras', icon: ShoppingCart, roles: ['superadmin'] },
  { to: '/clients', label: 'Clientes', icon: Users, roles: ['superadmin', 'sales'] },
  { to: '/partners', label: 'Partners', icon: Building2, roles: ['superadmin'] },
  { to: '/inventory', label: 'Inventario', icon: Package, roles: ['superadmin'] },
  { to: '/products', label: 'Producto', icon: Leaf, roles: ['superadmin'] },
  { to: '/reports', label: 'Reportes', icon: BarChart3, roles: ['superadmin'] },
  // Solo en dev. Vite inyecta `import.meta.env.DEV === true` en el build de
  // desarrollo; en producción este entry queda filtrado en tiempo de build.
  ...(import.meta.env.DEV
    ? [{ to: '/dev', label: 'Dev Tools', icon: Terminal, roles: ['superadmin'] }]
    : []),
]

export function Sidebar() {
  const { user } = useAuth()
  const role = user?.role || 'user'
  const items = MENU.filter((m) => m.roles.includes(role))

  return (
    <aside className="w-80 flex-shrink-0 bg-expo-dark shadow-md overflow-y-auto sticky top-[108px] h-[calc(100vh-108px)]">
      <nav className="p-6">
        <ul className="space-y-3">
          {items.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  [
                    'w-full flex items-center gap-3 px-6 py-4 text-base rounded-xl transition-all',
                    isActive
                      ? 'bg-expo-yellow text-expo-dark shadow-lg font-bold'
                      : 'text-white hover:bg-expo-primary font-medium',
                  ].join(' ')
                }
              >
                <Icon size={24} />
                <span className="whitespace-nowrap">{label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  )
}
